// # packages
import * as React from 'react';
import { boundMethod } from 'autobind-decorator';
import { cloneDeep, find, isEqual } from 'lodash';
// # interfaces
import { iPermission, iPermissionKey } from '@/interfaces/permissions';
import { iRole } from '@/interfaces/roles';
import { iRequestError } from '@/interfaces/errors';
// # components
import { SelectPermissions } from '@/components/permissions';
import { Message } from '@/components/feedback';
import { List } from '@/components/List';
import * as Tabs from '@/components/tabs';
// # service
import { rolesService } from '@/services/api';

interface iProps {
    /**
     * Optional callback fired after the role is successfully saved.
     * Role argument passed is the updated role from the API.
     */
    onSave?: (role: iRole) => any;
    /**
     * Optional callback fired after the role is successfully deleted.
     * Role argument is passed in case you need to identify which role it was.
     */
    onDelete?: (role: iRole) => any;
    /**
     * The permissions available for assigning to this role.
     */
    permissions: iPermission[];
    /**
     * The role to edit.
     * Don't pass a role to add a new one.
     */
    role?: iRole;
}

interface iState {
    flags: {
        deleteRequestInProgress: boolean;
        saveRequestInProgress: boolean;
    };
    role?: iRole;
    requestError?: iRequestError;
}

// an interface we can use for missing permissions
interface iPermissionWithWarning extends iPermission {
    warning?: string;
}

/**
 * Form for editing and saving a new or existing role.
 */
export class EditRole extends React.Component<iProps, iState> {
    /**
     * Default values to use when creating a new role.
     */
    static defaultRole: iRole = {
        id: undefined,
        key: undefined,
        label: '',
        legacy_role: 'member',
        permissions: [],
    };

    public state: iState = {
        flags: {
            deleteRequestInProgress: false,
            saveRequestInProgress: false,
        },
        // make a copy of the provided role, or the default role values
        role: Object.assign({}, this.props.role || EditRole.defaultRole),
    };

    /**
     * A copy of the initial role prop so we can compare and see if anything has been changed.
     */
    protected _initialRole: iRole = cloneDeep(this.state.role);

    /**
     * Public, read-only access to the initial role
     */
    public get initialRole(): iRole {
        return this._initialRole;
    }

    /**
     * Renders the content.
     *
     * @returns {React.ReactNode}
     */
    public render(): React.ReactNode {
        const { flags, role } = this.state;

        if (!role) {
            return (
                <div className="edit-role">
                    <p>No role loaded</p>
                </div>
            );
        }

        return (
            <div className="edit-role">
                <Tabs.Container initialTab="overview">
                    <Tabs.Tab id="overview">Overview</Tabs.Tab>
                    <Tabs.Tab id="permissions">Permissions</Tabs.Tab>
                    {role.id ? <Tabs.Tab id="delete" theme="danger">Delete</Tabs.Tab> : null}

                    <Tabs.Content id="overview">
                        {this.renderLabelInput()}
                        {this.renderLegacyRoleSelect()}
                        {this.renderSaveButton()}
                    </Tabs.Content>

                    <Tabs.Content id="permissions">
                        <label>Select permissions for this role</label>
                        <SelectPermissions
                            permissions={this.props.permissions}
                            onChange={this.handlePermissionsChange}
                            value={role.permissions}
                        />
                        {this.renderSaveButton()}
                    </Tabs.Content>

                    <Tabs.Content id="delete">
                        <p>Deleting this role is permanent and cannot be undone.</p>
                        <p>
                            Any users currently assigned to this role will lose their permissions.<br />
                            We recommend moving users to another role before deleting.
                        </p>

                        <br />

                        <button
                            className="btn-c2a style-danger edit-role__save"
                            disabled={flags.deleteRequestInProgress}
                            onClick={this.handleDelete}
                        >
                            {flags.deleteRequestInProgress ? 'Deleting...' : `Delete ${this.initialRole.label} Role`}
                        </button>
                    </Tabs.Content>
                </Tabs.Container>

                {this.renderValidationMessages()}
            </div>
        );
    }

    /**
     * Adds a permission to the role in this.state
     *
     * @param {iPermission} permission
     * @returns {void}
     */
    @boundMethod
    protected addPermission(permission: iPermission): void {
        const { role } = this.state;

        const permissionExists = role.permissions.includes(permission.key);
        if (permissionExists) { return; }

        this.setState(state => {
            const stateCopy = { ...state };
            stateCopy.role.permissions.push(permission.key);
            return stateCopy;
        });
    }

    /**
     * Gets a list of permissions that have been added to this role.
     * As we're only storing permission keys, this method finds the permissions they map to.
     *
     * Any permissions that cannot be found will have a "warning" property added, hence the iPermissionWithWarning type.
     *
     * @returns {iPermissionWithWarning[]}
     */
    @boundMethod
    protected getAppliedPermissions(): iPermissionWithWarning[] {
        const { permissions } = this.props;
        const { role } = this.state;

        // get original permissions from the permission keys stores in role.permissions
        const filteredPermissions: iPermissionWithWarning[] = role.permissions.map(key => {
            const permission = find(permissions, { key });

            // if the permission no longer exists, return a new object containing the key and a warning message
            return permission || {
                default_roles: [],
                feature: 'Missing',
                key: 'UNKNOWN_PERMISSION',
                label: '',
                warning: `"${key}" permission could not be found. It may no longer be supported.`,
            };
        });

        return filteredPermissions;
    }

    /**
     * Sends a delete request to the API, then resets component state.
     * Calls this.props.onDelete will be called with the deleted model.
     *
     * @returns {Promise<void>}
     */
    @boundMethod
    protected async handleDelete(): Promise<void> {
        const { onDelete } = this.props;
        const { role } = this.state;

        try {
            this.setState(state => ({
                flags: {
                    ...state.flags,
                    deleteRequestInProgress: true,
                },
            }));

            // take a copy of the role so we can determine which one was deleted later
            const roleCopy = cloneDeep(role);

            // delete from API
            await rolesService.delete(role);

            // reset state
            this.setState(state => ({
                flags: {
                    ...state.flags,
                    deleteRequestInProgress: false,
                },
                role: null,
            }), () => {
                // call onDelete prop
                if (onDelete) {
                    onDelete(cloneDeep(roleCopy));
                }
            });
        } catch (err) {
            this.setState(state => ({
                flags: {
                    ...state.flags,
                    deleteRequestInProgress: false,
                },
            }));

            return Promise.reject(err);
        }
    }

    /**
     * Updates component state when the role label input is changed.
     *
     * @param {React.ChangeEvent<HTMLInputElement>} event
     * @returns {void}
     */
    @boundMethod
    protected handleLabelChange(event: React.ChangeEvent<HTMLInputElement>): void {
        const value = event.target.value;
        this.setState(state => ({
            role: {
                ...state.role,
                label: value,
            },
        }));
    }

    /**
     * Updates component state when the legacy role field is changed.
     *
     * @param {React.ChangeEvent<HTMLSelectElement>} event
     * @returns {void}
     */
    @boundMethod
    protected handleLegacyRoleChange(event: React.ChangeEvent<HTMLSelectElement>): void {
        const value = event.target.value as iRole['legacy_role'];
        this.setState(state => ({
            role: {
                ...state.role,
                legacy_role: value,
            },
        }));
    }

    /**
     * Replaces permissions on the role.
     *
     * @param {iPermissionKey[]} permissions
     * @returns {void}
     */
    @boundMethod
    protected handlePermissionsChange(permissions: iPermissionKey[]): void {
        this.setState(state => {
            if (!state.role) { return state; }
            state.role.permissions = permissions;
            return state;
        });
    }

    /**
     * Saves the role to the API
     *
     * @returns {Promise<void>}
     */
    @boundMethod
    protected async handleSave(evt?: React.MouseEvent<HTMLButtonElement>): Promise<void> {
        if (evt) { evt.preventDefault(); }

        const { onSave } = this.props;
        const { role } = this.state;
        if (!this.hasChanges()) { return; }

        this.setState(state => ({
            flags: {
                ...state.flags,
                saveRequestInProgress: true,
            },
            requestError: null,
        }));

        try {
            // save role
            const updatedRole = await rolesService.save(role);

            // diff changes against the updated role
            this._initialRole = cloneDeep(updatedRole);

            // update state with updated role
            this.setState(state => ({
                flags: {
                    ...state.flags,
                    saveRequestInProgress: false,
                },
                role: updatedRole,
            }), () => {
                // call onSave() prop
                if (onSave) { onSave(updatedRole); }
            });
        } catch (error) {
            this.setState(state => ({
                flags: {
                    ...state.flags,
                    saveRequestInProgress: false,
                },
                requestError: error,
            }));
        }
    }

    /**
     * Whether this.state.role has been modified by the user
     *
     * @returns {boolean}
     */
    protected hasChanges(): boolean {
        const hasChanges = !isEqual(this.state.role, this.initialRole);
        return hasChanges;
    }

    /**
     * Makes a handler that removes a permission when called.
     * This is for performance reasons, so we don't have to call this.removePermission.bind
     * in the render function.
     *
     * @param {iPermission} permission
     * @returns {() => void}
     */
    @boundMethod
    protected makeRemovePermissionHandler(permission: iPermission): () => void {
        return () => {
            this.removePermission(permission);
        };
    }

    /**
     * Removes a permission from the role in this.state
     *
     * @param {iPermission} permission
     * @return {void}
     */
    protected removePermission(permission: iPermission): void {
        const { role } = this.state;

        const permissionsIndex = role.permissions.indexOf(permission.key);
        const permissionExists = permissionsIndex !== -1;
        if (!permissionExists) { return; }

        this.setState(state => {
            const stateCopy = { ...state };
            stateCopy.role.permissions.splice(permissionsIndex, 1);
            return state;
        });
    }

    /**
     * Renders a single item in the permission select dropdown.
     * We're specifying a custom renderer for this so we can disable items that have already been selected.
     *
     * @param {iPermission} permission
     * @param {() => any} clickHandler
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderPermissionDropdownItem(permission: iPermission, clickHandler: () => any): React.ReactElement {
        const { role } = this.state;
        const isSelected = role.permissions.includes(permission.key);

        // intercept click handler to disable clicks on selected items
        const handleClick = () => {
            if (!isSelected) {
                clickHandler();
            }
        };

        const classNames = [
            'list__item',
            'permissions-select__item',
            isSelected ? 'is-disabled' : 'is-interactive',
        ];

        return (
            <div className={classNames.join(' ')} onClick={handleClick}>
                {permission.label} {isSelected ? '(already added)' : ''}
                <span className="permissions-select__item-key">{permission.key}</span>
            </div>
        );
    }

    /**
     * Renders a single item in the list of selected permissions.
     *
     * @param {iPermissionWithWarning} permission
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderPermissionListItem(permission: iPermissionWithWarning): React.ReactElement {
        if (!permission) {
            return null;
        }

        return (
            <div className="list__item permissions-list__item">
                <div>
                    {permission.label}
                    {permission.warning ? (
                        <Message theme="warning">{permission.warning}</Message>
                    ) : null}
                    <span className="permissions-list__item-key">{permission.key}</span>
                </div>
                <button
                    className="permissions-list__remove-item-button"
                    onClick={this.makeRemovePermissionHandler(permission)}
                    title="Remove permission"
                >
                    <i className="fa fa-trash" />
                </button>
            </div>
        );
    }

    /**
     * Renders a list of permissions that have been assigned to the current role.
     *
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderPermissionList(): React.ReactNode {
        return (
            <List<iPermissionWithWarning>
                className="permissions-list"
                groupBy="feature"
                items={this.getAppliedPermissions()}
                renderItem={this.renderPermissionListItem}
            />
        );
    }

    /**
     * Renders the role name input field.
     *
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderLabelInput(): React.ReactNode {
        const { role } = this.state;

        return (
            <>
                <label htmlFor="role-name">Role Name</label>
                <input
                    autoFocus
                    className="form-control"
                    onChange={this.handleLabelChange}
                    name="role-name"
                    placeholder="The name of this role"
                    value={role.label}
                />
            </>
        );
    }

    /**
     * Renders the legacy role select dropdown field.
     *
     * @returns {React.ReactNode}
     */
    @boundMethod
    protected renderLegacyRoleSelect(): React.ReactNode {
        const { role } = this.state;

        return (
            <>
                <label htmlFor="role-legacy-role">Legacy Role</label>
                <select
                    className="form-control"
                    name="role-legacy-role"
                    onChange={this.handleLegacyRoleChange}
                    value={role.legacy_role}
                >
                    <option value="admin">Admin</option>
                    <option value="member">Member</option>
                </select>
            </>
        );
    }

    /**
     * Renders the save button.
     *
     * @returns {React.ReactNode}
     */
    protected renderSaveButton(): React.ReactNode {
        const { flags } = this.state;

        return (
            <button
                className="btn-c2a style-secondary edit-role__save"
                disabled={flags.saveRequestInProgress || !this.hasChanges()}
                onClick={this.handleSave}
            >
                {flags.saveRequestInProgress ? 'Saving...' : 'Save'}
            </button>
        );
    }

    /**
     * Renders a list of validation messages from this.state.requestError
     *
     * @returns {React.ReactNode}
     */
    protected renderValidationMessages(): React.ReactNode {
        const { requestError } = this.state;
        if (!requestError) { return null; }

        return (
            <>
                <span className="form-error">{requestError.message}</span>
                {requestError.fields ? requestError.fields.map(error => (
                    error.messages.map(message => <span className="form-error">{message}</span>)
                )) : null}
            </>
        );
    }
}

export default EditRole;
